import { memo, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  PLATE_SCALE_MAX_PX,
  PLATE_VINEYARD_MIN_ZOOM,
  WINERY_SIZE_NOT_STATED_LABEL,
} from '../../lib/chrome_config'
import { COVERAGE_DRAWN, COVERAGE_NAMED, COVERAGE_NONE, WINERY_UNKNOWN_COLOR } from '../../lib/map_config'
import {
  ClusterChip,
  CountrySwatch,
  RibbonSwatch,
  SelectedCountrySwatch,
  SelectedSwatch,
  TastingCount,
  VineyardSwatch,
  WineryDot,
  WinerySizeRamp,
} from './plate_key'
import type { Geometry, Position } from 'geojson'
import type { AtlasLayer, DetailLayer, MapViewport, WineryLayer } from '../../types/atlas'

// Web Mercator, as MapLibre lays it out: the world is one 512px tile at zoom 0,
// and the projection stops at this latitude.
const EARTH_CIRCUMFERENCE_M = 40_075_016.686
const WORLD_TILE_PX = 512
const MERCATOR_MAX_LATITUDE = 85.051129

// Every distance a scale bar may read: 1, 2 or 5 times a power of ten, from a
// metre to fifty thousand kilometres, longest first.
const ROUND_DISTANCES_M = [7, 6, 5, 4, 3, 2, 1, 0].flatMap((power) => [5, 2, 1].map((digit) => digit * 10 ** power))

// An explicit locale, as everywhere else in the chrome: "2,000 km" must not
// become "2.000 km" under a viewer's own.
const number = new Intl.NumberFormat('en-US')

const radians = (degrees: number) => (degrees * Math.PI) / 180

// The latitude at the MIDDLE OF THE SCREEN, from the frame's two edges. Not
// their mean: Mercator stretches towards the poles, so the middle row of a
// frame from 30° to 60° is at about 47°, and a bar measured at 45° would be 4%
// short.
function frameLatitude({ south, north }: MapViewport) {
  const y = (latitude: number) => {
    const clamped = Math.max(-MERCATOR_MAX_LATITUDE, Math.min(MERCATOR_MAX_LATITUDE, latitude))

    return Math.log(Math.tan(Math.PI / 4 + radians(clamped) / 2))
  }

  return (Math.atan(Math.sinh((y(south) + y(north)) / 2)) * 180) / Math.PI
}

export interface PlateScale {
  metres: number
  /** How long the bar is drawn, in CSS pixels. */
  pixels: number
  label: string
}

/**
 * The scale bar for a settled frame: the longest round distance that fits in
 * `maxPixels`, and how many pixels that distance is at the middle of the
 * screen.
 *
 * A Mercator map has no one scale. At the world view the same bar is twice as
 * long at 60° as on the equator, which is why it is measured where the reader
 * is looking — the middle — and why it is a guide there rather than a ruler.
 * From appellation zoom in, the difference across a screen is under a percent.
 */
export function plateScale(viewport: MapViewport, maxPixels = PLATE_SCALE_MAX_PX): PlateScale {
  const metresPerPixel =
    (EARTH_CIRCUMFERENCE_M * Math.cos(radians(frameLatitude(viewport)))) / (WORLD_TILE_PX * 2 ** viewport.zoom)
  const metres =
    ROUND_DISTANCES_M.find((distance) => distance / metresPerPixel <= maxPixels) ??
    ROUND_DISTANCES_M[ROUND_DISTANCES_M.length - 1]

  return {
    metres,
    pixels: Math.max(1, Math.round(metres / metresPerPixel)),
    label: metres >= 1000 ? `${number.format(metres / 1000)} km` : `${metres} m`,
  }
}

// The box of every part of a shape, kept against the geometry object it was
// worked out for. A shape is walked once, the first time a key asks about it,
// and after that "is it in the frame" is four comparisons a part.
type PartBox = [west: number, south: number, east: number, north: number]

const boxes = new WeakMap<Geometry, PartBox[]>()

function ringBox(ring: Position[]): PartBox {
  let west = Infinity
  let south = Infinity
  let east = -Infinity
  let north = -Infinity

  for (const [lng, lat] of ring) {
    if (lng < west) west = lng
    if (lng > east) east = lng
    if (lat < south) south = lat
    if (lat > north) north = lat
  }

  return [west, south, east, north]
}

function partBoxes(geometry: Geometry): PartBox[] {
  let known = boxes.get(geometry)
  if (known) return known

  if (geometry.type === 'Polygon') known = [ringBox(geometry.coordinates[0] ?? [])]
  else if (geometry.type === 'MultiPolygon') known = geometry.coordinates.map((polygon) => ringBox(polygon[0] ?? []))
  else if (geometry.type === 'Point') {
    const [lng, lat] = geometry.coordinates
    known = [[lng, lat, lng, lat]]
  } else known = []

  boxes.set(geometry, known)

  return known
}

/**
 * Whether any part of a shape's box is in the frame.
 *
 * PART BY PART, never the box of the whole: the United States with the
 * Aleutians, and five other countries, straddle the antimeridian, and the box
 * of the whole of one of those is the whole world — which would key "drawn" on
 * a view of Chile. A box test rather than the outline itself, because it is
 * what a key needs: a shape whose box is in the frame and whose line just
 * misses it is a row too many, where walking every outline on every settle
 * would be real work for one swatch.
 */
export function inFrame(geometry: Geometry, frame: MapViewport): boolean {
  return partBoxes(geometry).some(
    ([west, south, east, north]) =>
      west <= frame.east && east >= frame.west && south <= frame.north && north >= frame.south,
  )
}

/**
 * How many levels of appellation are IN THE FRAME at this zoom: 0 to 3.
 *
 * Depth is the tier's rank among the tiers the data holds — the same rule the
 * map uses to pick a ribbon — and a tier is drawn from its own `min_zoom` up.
 * So this is the number of distinct tiers at or below the camera among the
 * appellations whose ground is in the frame, capped at the three the ribbon
 * has.
 *
 * It counted what the shell HELD, which is every frame visited: a reader who
 * panned from Napa to the Nevada desert was still told what a nested ribbon
 * means, over a screen with no appellation on it. That was on the reasoning
 * that asking which of 1,800 polygons intersect the frame on every settle was
 * the wrong price for one swatch — and it would be, by outline. By box, kept
 * per shape (`inFrame`), it is a few thousand comparisons once per settle, and
 * the key says what the map is showing.
 */
export function drawnDepths(layer: DetailLayer | null, frame: MapViewport | null): number {
  if (!layer || !frame) return 0

  const tiers = new Set<number>()
  for (const feature of layer.features) {
    const tier = feature.properties.min_zoom
    if (typeof tier !== 'number' || tier > frame.zoom || tiers.has(tier)) continue
    if (inFrame(feature.geometry, frame)) tiers.add(tier)
    if (tiers.size >= 3) break
  }

  return Math.min(tiers.size, 3)
}

/** The country states that are in the frame: what the country rows key. */
export function framedCoverages(layer: AtlasLayer | null, frame: MapViewport | null): ReadonlySet<string> {
  const coverages = new Set<string>()
  if (!layer || !frame) return coverages

  for (const feature of layer.features) {
    if (coverages.has(feature.properties.coverage)) continue
    if (inFrame(feature.geometry, frame)) coverages.add(feature.properties.coverage)
  }

  return coverages
}

export interface PlateKeyState {
  zoom: number
  /** Where appellations start being drawn (`map_zoom.detail`). */
  detailZoom: number
  /** The country states in the frame, or none when countries are not drawn. */
  coverages: ReadonlySet<string>
  /** How many levels of appellation are in the frame. */
  depths: number
  wineries: WineryLayer['mode'] | null
  /** Whether a dot in the frame has a size class: what the ramp is the key to. */
  sized?: boolean
  /** The reader's own tastings are drawn. */
  tastings?: boolean
  selected: 'place' | 'country' | 'winery' | null
}

export interface PlateKeyEntry {
  id: string
  label: string
  swatch: ReactNode
  /** Where the entry is DRAWN, left to right — which is not the order it is ranked in. */
  order: number
}

const COUNTRY_ROWS = [
  { coverage: COVERAGE_DRAWN, label: 'Appellations drawn' },
  { coverage: COVERAGE_NAMED, label: 'Imported, no boundaries published' },
  { coverage: COVERAGE_NONE, label: 'No appellations yet' },
]

const DEPTH_LABELS = ['Appellation', 'nested in it', 'nested deeper']

// The drawing order: the selection, then areas outermost first, then points,
// then ground. A key reads in the order the eye meets the marks, whatever
// order they were chosen in.
const ORDER = { selected: 0, area: 10, ramp: 20, unknown: 21, cluster: 22, tastings: 25, ground: 30 }

/**
 * The key for what is on screen NOW, in RANK order: the first entry is the
 * last to be given up when the strip runs out of room (see `PlateRibbon`).
 *
 * The ranking is by what a reader is most likely to be asking about:
 *
 * 1. The selection, because it is the one thing they have just done. A
 *    COUNTRY's is a mark of its own: the map draws a soft band inside its
 *    coast, not the cased ribbon, and the key shows the one it draws.
 * 2. What carries the view: the country states below the zoom where
 *    appellations appear, the appellation ribbon from there in.
 * 3. The reader's own tastings, when they are on the map: the one mark here
 *    that is theirs, and until it had a row the heaviest thing on a signed-in
 *    map was in no key at all.
 * 4. The winery marks — and the neutral dot first. Nearly every dot on the map
 *    is the neutral one, so "what is this dark dot" is the question. The
 *    neutral dot's label says "size" for that reason: it has to say what is
 *    not stated when the ramp beside it has been given up.
 * 5. The nested ribbons, a refinement of something already keyed — and then
 *    the SIZE RAMP, when no dot in the frame has a class. It used to outrank
 *    them always, and over the Santa Ynez, where three depths of ribbon are on
 *    screen and every one of seventy dots is neutral, the strip spent its room
 *    on four teal dots that were nowhere on the map and dropped "nested
 *    deeper". With a classed dot in the frame the ramp is the key to it and
 *    goes back above the nested rows.
 * 6. The ground.
 *
 * EVERY ROW IS FOR SOMETHING IN THE FRAME. Countries are keyed by the states
 * of the countries in view, appellation depths by the appellations in view
 * (`drawnDepths`), and the vineyard row only where there is an appellation to
 * have vines in — the basemap's vines are not something the shell can ask
 * about, and wine country is where they are. It used to key what the shell
 * held, and so listed "Appellation" and "Vineyard" over the Nevada desert.
 *
 * Countries are keyed only BELOW `detailZoom`. Their tint is still on the map
 * for a zoom or two past it, receding, but from there the appellations say
 * what the tint was saying, and two keys for one question is one too many.
 *
 * The neutral dot appears whenever dots are drawn, and the ramp with it: the
 * ramp beside the neutral is what tells a reader that the neutral is the
 * absence of a class and not a class of its own.
 */
export function plateKey({
  zoom,
  detailZoom,
  coverages,
  depths,
  wineries,
  sized = false,
  tastings = false,
  selected,
}: PlateKeyState): PlateKeyEntry[] {
  const entries: PlateKeyEntry[] = []
  const appellations = zoom >= detailZoom ? depths : 0

  if (selected === 'place') {
    entries.push({ id: 'selected', label: 'Selected', swatch: <SelectedSwatch />, order: ORDER.selected })
  }
  if (selected === 'country') {
    entries.push({ id: 'selected', label: 'Selected', swatch: <SelectedCountrySwatch />, order: ORDER.selected })
  }
  if (selected === 'winery') {
    entries.push({
      id: 'selected',
      label: 'Selected',
      swatch: <WineryDot color={WINERY_UNKNOWN_COLOR} selected className="mx-1" />,
      order: ORDER.selected,
    })
  }

  if (zoom < detailZoom) {
    COUNTRY_ROWS.filter((row) => coverages.has(row.coverage)).forEach((row, index) => {
      entries.push({
        id: `country-${row.coverage}`,
        label: row.label,
        swatch: <CountrySwatch coverage={row.coverage} />,
        order: ORDER.area + index,
      })
    })
  } else if (appellations > 0) {
    entries.push({ id: 'depth-0', label: DEPTH_LABELS[0], swatch: <RibbonSwatch depth={0} />, order: ORDER.area })
  }

  if (tastings) {
    entries.push({ id: 'tastings', label: 'Your tastings', swatch: <TastingCount />, order: ORDER.tastings })
  }

  const ramp: PlateKeyEntry = {
    id: 'size-ramp',
    label: 'Winery size, micro–large',
    swatch: <WinerySizeRamp />,
    order: ORDER.ramp,
  }

  if (wineries === 'points') {
    entries.push({
      id: 'size-unknown',
      label: `size ${WINERY_SIZE_NOT_STATED_LABEL.toLowerCase()}`,
      swatch: <WineryDot color={WINERY_UNKNOWN_COLOR} />,
      order: ORDER.unknown,
    })
    if (sized) entries.push(ramp)
  }

  if (wineries === 'clusters') {
    entries.push({ id: 'cluster', label: 'Wineries nearby, counted', swatch: <ClusterChip />, order: ORDER.cluster })
  }

  for (let depth = 1; depth < appellations; depth += 1) {
    entries.push({
      id: `depth-${depth}`,
      label: DEPTH_LABELS[depth],
      swatch: <RibbonSwatch depth={depth} />,
      order: ORDER.area + depth,
    })
  }

  if (wineries === 'points' && !sized) entries.push(ramp)

  if (zoom >= PLATE_VINEYARD_MIN_ZOOM && appellations > 0) {
    entries.push({ id: 'vineyard', label: 'Vineyard', swatch: <VineyardSwatch />, order: ORDER.ground })
  }

  return entries
}

interface PlateRibbonProps {
  /** The settled frame, or null until the map has reported one — and then there is no ribbon. */
  viewport: MapViewport | null
  detailZoom: number
  /** Each layer as the map is DRAWING it: null when it is switched off or has not arrived. */
  worldLayer: AtlasLayer | null
  regionLayer: DetailLayer | null
  wineryLayer: WineryLayer | null
  /** Whether the reader's own tastings are drawn: signed in, switched on, and some to draw. */
  tastings?: boolean
  /** What is drawn in the selection colour, if anything. */
  selected: 'place' | 'country' | 'winery' | null
}

// The strip's own metrics, as numbers, because the fitting below does
// arithmetic with them. They are applied as inline styles from these same
// constants, so a class and a number cannot drift apart.
const STRIP_PADDING_PX = 11
const STRIP_GAP_PX = 9
const DIVIDER_PX = 1

// What the strip is showing, and for which key that was worked out. `shown` is
// null while nothing has been measured, which means "everything".
interface Fit {
  signature: string
  /** Whether even the scale fits. If not, the strip is not drawn at all. */
  scale: boolean
  shown: string | null
}

/**
 * The plate's own furniture: a scale bar and a key, as one quiet paper strip
 * at the bottom left of the map.
 *
 * The full key lives in the Layers popover and is twenty rows long. This is
 * the part of it that applies to what is on screen at this moment — the
 * country states at the world view, the ribbon depths once appellations are
 * drawn, the winery marks once those are — so a reader looking at a narrow
 * dark ribbon inside a wide pale one can find out what it means without
 * opening anything.
 *
 * WHERE IT SITS, AND WHEN IT IS NOT THERE. It is the first item of the map's
 * footer, on the attribution's row, so it moves left of an open panel exactly
 * as the credit does and needs no reservation of its own. THE CREDIT COMES
 * FIRST ON THAT ROW — the licence requires it and nothing requires this — so
 * the strip takes what the credit leaves, and the rule has two halves. (What
 * the credit gives up for the strip is only its one-line FORM: on a row that
 * could hold that line and no key beside it, the credit is set in its compact
 * block instead, every word still there. The footer in atlas_layout.tsx has
 * that half, and the number.)
 *
 * - Below 34rem of row there is no ribbon: every phone, and a tablet beside
 *   an open panel. That is the width under which the credit's compact block
 *   (22.5rem at its widest) and a scale bar cannot share a row, said as a
 *   container query so it costs nothing to apply. It was 45rem while that
 *   block was up to 36rem wide, which left a small laptop beside a panel with
 *   no scale at all. On a phone the credit is two lines riding on a sheet; a
 *   strip there as well would cover a tenth of what is left of the map.
 * - From there up, it is drawn when the scale bar fits WHOLE in what the
 *   credit leaves, with as many key entries as also fit, taken in the order
 *   `plateKey` ranks them and drawn in reading order. An entry is whole or
 *   absent, never cut; and if a wide typeface makes the credit long enough to
 *   leave less than the scale needs, there is no strip rather than half a one.
 *
 * IT COSTS NOTHING WHILE THE MAP MOVES. Everything it reads is state the shell
 * already holds and changes only when the camera comes to REST or a layer
 * arrives: the settled frame, the three layers, the selection. Nothing here
 * listens to the map, the pointer or an animation frame, and the component is
 * memoised on those values, so the shell re-rendering for a popover does not
 * re-render it. The scale therefore updates when a zoom ends, not during it —
 * a bar that chased the camera would be a React commit per frame for a number
 * nobody reads mid-gesture.
 *
 * The fitting measures the DOM: each entry's width once per change of the key,
 * and the room once per change of the room (a `ResizeObserver` on the space
 * the credit leaves). CSS alone can clip a row of entries, but it cannot
 * shrink the strip round the ones that are left, and a paper strip with a
 * blank tail reads as a rendering fault.
 *
 * IT TAKES NO POINTER EVENTS FROM THE MAP EXCEPT ON ITSELF. The room it is
 * measured in spans the width the credit leaves and is transparent to the
 * pointer, like the footer around it; only the strip turns events back on, so
 * a drag that starts beside it still pans.
 */
function PlateRibbon({
  viewport,
  detailZoom,
  worldLayer,
  regionLayer,
  wineryLayer,
  tastings = false,
  selected,
}: PlateRibbonProps) {
  const room = useRef<HTMLDivElement>(null)
  const scaleBox = useRef<HTMLParagraphElement>(null)
  const list = useRef<HTMLUListElement>(null)
  const widths = useRef(new Map<string, number>())

  const zoom = viewport?.zoom ?? 0

  // What is in the FRAME, of what the shell holds. Each is worked out when the
  // camera comes to rest or a layer arrives, never while it moves, and the
  // country states are not asked for at all once the countries are not keyed.
  const keysCountries = zoom < detailZoom
  const coverageList = useMemo(
    () => [...framedCoverages(keysCountries ? worldLayer : null, viewport)].sort().join(' '),
    [worldLayer, viewport, keysCountries],
  )
  const coverages = useMemo(() => new Set(coverageList ? coverageList.split(' ') : []), [coverageList])
  const depths = useMemo(() => drawnDepths(regionLayer, viewport), [regionLayer, viewport])

  // Only a layer with something in it draws a mark to key.
  const marks = wineryLayer?.mode === 'points' ? wineryLayer.wineries.length : (wineryLayer?.clusters.length ?? 0)
  const wineries = wineryLayer && marks > 0 ? wineryLayer.mode : null
  const sized = useMemo(
    () => wineryLayer?.mode === 'points' && wineryLayer.wineries.some((winery) => winery.size_class !== null),
    [wineryLayer],
  )

  const ranked = useMemo(
    () => plateKey({ zoom, detailZoom, coverages, depths, wineries, sized, tastings, selected }),
    [zoom, detailZoom, coverages, depths, wineries, sized, tastings, selected],
  )
  const scale = useMemo(() => (viewport ? plateScale(viewport) : null), [viewport])

  // Which key this is, as a string: what the measured widths belong to.
  const signature = ranked.map((entry) => entry.id).join(' ')

  // A fit worked out for any other key is stale. Until this key has been
  // measured EVERY entry is rendered — that render is what gets measured, and
  // it is replaced before the browser paints, because a state update inside a
  // layout effect is flushed synchronously.
  const [fitted, setFitted] = useState<Fit | null>(null)
  const fit = fitted?.signature === signature ? fitted : null
  const chosen = fit?.shown == null ? null : new Set(fit.shown.split(' '))
  const entries = ranked
    .filter((entry) => chosen === null || chosen.has(entry.id))
    .sort((first, second) => first.order - second.order)

  const scaleLabel = scale?.label
  const scalePixels = scale?.pixels

  useLayoutEffect(() => {
    const roomElement = room.current
    if (!roomElement) return

    // This commit rendered the whole key: measure it. A commit that follows a
    // fit rendered only what fitted, and keeps the widths it has.
    const items = [...(list.current?.children ?? [])] as HTMLElement[]
    if (items.length === ranked.length) {
      widths.current = new Map(items.map((item) => [item.dataset.entry ?? '', item.offsetWidth]))
    }

    const refit = () => {
      const available = roomElement.clientWidth
      let next: Fit

      if (available === 0) {
        // No layout to measure against: jsdom, or the room is `display: none`
        // because the row is too narrow for a ribbon. Nothing is cut that
        // cannot be seen.
        next = { signature, scale: true, shown: null }
      } else {
        // What the strip spends on the scale and its own two ends.
        const scaleEnd = (scaleBox.current?.offsetLeft ?? 0) + (scaleBox.current?.offsetWidth ?? 0)
        const taken: string[] = []
        let used = scaleEnd + STRIP_PADDING_PX

        // In rank order, stopping at the first that does not fit: a lower
        // entry never takes the room a higher one was refused.
        for (const entry of ranked) {
          const width = widths.current.get(entry.id) ?? Infinity
          const cost = STRIP_GAP_PX + width + (taken.length === 0 ? DIVIDER_PX + STRIP_GAP_PX : 0)

          if (used + cost > available) break
          used += cost
          taken.push(entry.id)
        }

        next = { signature, scale: scaleEnd + STRIP_PADDING_PX <= available, shown: taken.join(' ') }
      }

      setFitted((current) =>
        current?.signature === next.signature && current.scale === next.scale && current.shown === next.shown
          ? current
          : next,
      )
    }

    refit()

    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(refit)
    observer.observe(roomElement)

    return () => observer.disconnect()
    // `ranked` changes exactly when `signature` does. The scale is here
    // because the bar and its label change width with the zoom, and what they
    // take the entries lose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, scaleLabel, scalePixels])

  if (!scale) return null

  return (
    <div
      ref={room}
      data-testid="plate-ribbon"
      role="group"
      aria-label="Scale and key"
      className="hidden min-w-0 flex-1 @[34rem]:flex"
    >
      <div
        className={`pointer-events-auto relative flex h-[26px] max-w-full items-center overflow-hidden rounded-full bg-surface-raised text-[11px] leading-none text-ink-soft shadow-chrome ring-1 ring-black/5 ${
          fit && !fit.scale ? 'invisible' : ''
        }`}
        style={{ paddingInline: STRIP_PADDING_PX, columnGap: STRIP_GAP_PX }}
      >
        <p ref={scaleBox} data-testid="plate-scale" className="flex shrink-0 items-center gap-2 tabular-nums">
          {/* A surveyor's bar: a baseline with a tick at each end, as long as
              the distance beside it is on the ground. */}
          <span
            aria-hidden="true"
            className="block h-[5px] border-x border-b border-ink-soft/70"
            style={{ width: scale.pixels }}
          />
          <span className="sr-only">Scale: </span>
          {scale.label}
        </p>

        {entries.length > 0 && (
          <>
            <span aria-hidden="true" className="h-3.5 shrink-0 bg-black/10" style={{ width: DIVIDER_PX }} />
            <ul ref={list} data-testid="plate-key" className="flex min-w-0 items-center" style={{ columnGap: STRIP_GAP_PX }}>
              {entries.map((entry) => (
                <li key={entry.id} data-entry={entry.id} className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
                  {entry.swatch}
                  {entry.label}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}

export default memo(PlateRibbon)
